import { createQueryOptions, useQuery } from '@connectrpc/connect-query'
import { create } from '@bufbuild/protobuf'
import type { Transport } from '@connectrpc/connect'
import { AI_ORB_STATE_DEFAULTS, type AiOrbConfig, type OrbState } from '@cortado/orb'
import { getAiOrbConfig } from '@/gen/cortado/v1/agent-AgentService_connectquery'
import { GetAiOrbConfigRequestSchema } from '@/gen/cortado/v1/agent_pb'
import type { AiOrbConfig as AiOrbConfigProto } from '@/gen/cortado/v1/settings_pb'
import { connectTransport } from '@/lib/connect-transport'

/**
 * Query options for the GetAiOrbConfig RPC, for use in route loaders via
 * queryClient.ensureQueryData/prefetchQuery. Uses the same connectTransport
 * singleton as useAiOrbConfig so the cache key matches.
 */
export function getAiOrbConfigQueryOptions(transport: Transport = connectTransport) {
  return createQueryOptions(getAiOrbConfig, create(GetAiOrbConfigRequestSchema, {}), { transport })
}

function protoToOrbConfig(remote: AiOrbConfigProto, fallback: AiOrbConfig): AiOrbConfig {
  return {
    colors: remote.colors.length >= 2 ? remote.colors : fallback.colors,
    duration: remote.duration,
    blobScale: remote.blobScale,
    flow: remote.flow,
    turbulence: remote.turbulence,
    shift: remote.shift,
    balance: remote.balance,
    chroma: remote.chroma,
    contrast: remote.contrast,
    rim: remote.rim,
    specular: remote.specular,
    inner: remote.inner,
    wobble: remote.wobble,
    iridescence: remote.iridescence,
    shading: remote.shading,
    refraction: remote.refraction,
    lightX: remote.lightX,
    lightY: remote.lightY,
    aberration: remote.aberration,
    grain: remote.grain,
    softness: remote.softness,
    glow: remote.glow,
  }
}

/**
 * Resolves the platform-wide AI orb config for each orb state. Each state
 * (idle/working/ready) is independently saved server-side; an unset state, a
 * still-loading query, or a fetch error all resolve that state to its own
 * built-in defaults so the orb always renders.
 */
export function useAiOrbConfig(): Record<OrbState, AiOrbConfig> {
  const query = useQuery(getAiOrbConfig, create(GetAiOrbConfigRequestSchema, {}))
  const states = query.data?.states
  return {
    idle: states?.idle ? protoToOrbConfig(states.idle, AI_ORB_STATE_DEFAULTS.idle) : AI_ORB_STATE_DEFAULTS.idle,
    working: states?.working
      ? protoToOrbConfig(states.working, AI_ORB_STATE_DEFAULTS.working)
      : AI_ORB_STATE_DEFAULTS.working,
    ready: states?.ready ? protoToOrbConfig(states.ready, AI_ORB_STATE_DEFAULTS.ready) : AI_ORB_STATE_DEFAULTS.ready,
  }
}
